import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import KeyboardComponent from "../Components/Caisse/keyboards/virtualKeypad";
import { buildEnvironment } from "../Services/UserApi";

const ORG_TYPES = ["Magasin", "Restaurant", "Catalogue", "Showroom", "Events"];

const FIELD_ORDER = [
  "org.nom",
  "org.numero",
  "org.email",
  "org.address",
  "org.matricule_fiscale",
  "admin.nom",
  "admin.prenom",
  "admin.email",
  "admin.mobile",
  "admin.password",
];

const FIELD_LABELS = {
  "org.nom": "Nom de l'organisation *",
  "org.numero": "Téléphone",
  "org.email": "Email de l'organisation",
  "org.address": "Adresse",
  "org.matricule_fiscale": "Matricule fiscale",
  "admin.nom": "Nom *",
  "admin.prenom": "Prénom *",
  "admin.email": "Email *",
  "admin.mobile": "Mobile",
  "admin.password": "Mot de passe *",
};

// These are typed in lowercase even though the keypad only emits uppercase letters.
const LOWERCASED_FIELDS = ["org.email", "admin.email", "admin.password"];

function RegisterEnvironment() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    org: {
      nom: "",
      type: "Magasin",
      numero: "",
      email: "",
      address: "",
      matricule_fiscale: "",
    },
    admin: {
      nom: "",
      prenom: "",
      email: "",
      mobile: "",
      password: "",
      gender: "homme",
    },
  });
  const [activeField, setActiveField] = useState("org.nom");
  const [showLetters, setShowLetters] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const getFieldValue = (path) => {
    const [section, field] = path.split(".");
    return formData[section][field];
  };

  const setFieldValue = (path, updater) => {
    const [section, field] = path.split(".");
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]:
          typeof updater === "function"
            ? updater(prev[section][field])
            : updater,
      },
    }));
  };

  const handleSubmit = async () => {
    if (isProcessing) return;

    const { org, admin } = formData;
    if (!org.nom.trim()) {
      toast.error("Le nom de l'organisation est requis");
      setActiveField("org.nom");
      return;
    }
    if (
      !admin.nom.trim() ||
      !admin.prenom.trim() ||
      !admin.email.trim() ||
      !admin.password.trim()
    ) {
      toast.error(
        "Veuillez remplir les champs obligatoires du compte administrateur",
      );
      return;
    }

    setIsProcessing(true);
    try {
      const response = await buildEnvironment({ organisation: org, admin });

      if (response?.data?.success) {
        toast.success("Environnement créé avec succès ! Connectez-vous.");
        setTimeout(() => navigate("/login"), 1200);
      } else {
        toast.error(
          response?.data?.message ||
            "Erreur lors de la création de l'environnement",
        );
      }
    } catch (error) {
      console.error("Build environment error:", error);
      toast.error("Erreur lors de la création de l'environnement");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyPress = (key) => {
    if (isProcessing) return;

    if (key === "←") {
      setFieldValue(activeField, (v) => v.slice(0, -1));
      return;
    }

    if (key === "✔") {
      const index = FIELD_ORDER.indexOf(activeField);
      if (index >= 0 && index < FIELD_ORDER.length - 1) {
        setActiveField(FIELD_ORDER[index + 1]);
      } else {
        handleSubmit();
      }
      return;
    }

    const char =
      /^[A-Z]$/.test(key) && LOWERCASED_FIELDS.includes(activeField)
        ? key.toLowerCase()
        : key;
    setFieldValue(activeField, (v) => v + char);
  };

  const renderField = (path, placeholder, type = "text") => (
    <div
      key={path}
      className={`reg-field${activeField === path ? " active" : ""}`}
      onClick={() => setActiveField(path)}
    >
      <span className="reg-field-label">{FIELD_LABELS[path]}</span>
      <input
        type={type}
        value={getFieldValue(path)}
        placeholder={placeholder}
        readOnly
        inputMode="none"
        disabled={isProcessing}
      />
    </div>
  );

  return (
    <div className="register-container">
      <ToastContainer />
      <div className="register-form-panel">
        <h2>Créer votre espace Scaniny</h2>

        <div className="reg-section">
          <h3>Organisation</h3>
          {renderField("org.nom", "ex: Café Le Soleil")}
          <div className="reg-pill-group">
            {ORG_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                className={`reg-pill${formData.org.type === t ? " active" : ""}`}
                onClick={() => setFieldValue("org.type", t)}
                disabled={isProcessing}
              >
                {t}
              </button>
            ))}
          </div>
          {renderField("org.numero", "Téléphone")}
          {renderField("org.email", "contact@organisation.com")}
          {renderField("org.address", "Adresse")}
          {renderField("org.matricule_fiscale", "Optionnel")}
        </div>

        <div className="reg-section">
          <h3>Compte administrateur</h3>
          {renderField("admin.nom", "Nom")}
          {renderField("admin.prenom", "Prénom")}
          {renderField("admin.email", "admin@organisation.com")}
          {renderField("admin.mobile", "Mobile")}
          {renderField("admin.password", "Mot de passe", "password")}
          <div className="reg-pill-group">
            {["homme", "femme"].map((g) => (
              <button
                key={g}
                type="button"
                className={`reg-pill${formData.admin.gender === g ? " active" : ""}`}
                onClick={() => setFieldValue("admin.gender", g)}
                disabled={isProcessing}
              >
                {g === "homme" ? "Homme" : "Femme"}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="btn-submit"
          onClick={handleSubmit}
          disabled={isProcessing}
        >
          {isProcessing ? "Création en cours..." : "Créer l'environnement"}
        </button>

        <p className="signup-text">
          Déjà inscrit ? <Link to="/login">Se connecter</Link>
        </p>
      </div>

      <div className="register-keyboard-panel">
        <KeyboardComponent
          context="form"
          onKeyPress={handleKeyPress}
          showLetters={showLetters}
          setShowLetters={setShowLetters}
          disabled={isProcessing}
        />
      </div>
    </div>
  );
}

export default RegisterEnvironment;
